import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';
import { StaffedRoleService } from '../../../../core/services/staffed-role.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StaffedRole } from '../../../../core/models/staffedRole';
import { RegexConstants } from '../../../../core/constants/regex-constants';
import { InputErrorMessageComponent } from '../../../../shared/components/input-error-message/input-error-message.component';
import { ErrorMessages } from '../../../../core/constants/error-messages';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { InputNumberModule } from 'primeng/inputnumber';

@Component({
  selector: 'em-staffed-role-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule,InputNumberModule, ButtonModule, ToastModule, InputErrorMessageComponent, DropdownModule,FormsModule,TranslatePipe],
  templateUrl: './staffed-role-form.component.html',
  styleUrl: './staffed-role-form.component.scss',
  providers: [MessageService],
})
export class StaffedRoleFormComponent {
  form!: FormGroup;
  formErrors: any;
  isEditMode: boolean = false;
  errorMessages: any = { ...ErrorMessages.staffedRole };
  translations: any = {};
  roleOptions: string[] = [];
  roleOptionsObj: { label: string; value: string; searchKey: string }[] = [];
  filteredRoleOptionsObj: { label: string; value: string; searchKey: string }[] = [];
  filterValue: string = '';
  orderedCurrencies: string[] = [];
  fiscalYearOptions: any[] = [];
  constructor(
    private fb: FormBuilder,
    private staffedRoleService: StaffedRoleService,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
    private config: DynamicDialogConfig,
    private translate: TranslateService,
  ) { }


  
  ngOnInit() {
    this.loadTranslations();
    this.orderedCurrencies = this.config.data?.currencies || [];
    const currencyControls: any = {};
    this.orderedCurrencies.forEach(currency => {
    const controlName =
      'rate' + currency.charAt(0).toUpperCase() + currency.slice(1).toLowerCase();
    currencyControls[controlName] = [null, [Validators.min(0)]];
   });

  this.form = this.fb.group({
    id: [null],
    role: [null, [Validators.required, Validators.pattern(/^[a-zA-Z0-9\s\-]*$/)]],
    fiscalYear: [null, [Validators.required]],
    rateEuro: [null, [Validators.required, Validators.min(0)]],
    ...currencyControls,
  });
  const startFY = 25;
  const endFY = 30;
  this.fiscalYearOptions = Array.from({ length: endFY - startFY + 1 }, (_, i) => {
    const fy = startFY + i;
    return { label: 'FY' + fy, value: 'FY' + fy };
  });

  // EDIT mode
  if (this.config.data && this.config.data.id) {
    this.isEditMode = true;
    this.populateForm(this.config.data);
    this.form.get('role')?.disable();
  }

  // ALWAYS load all roles (independent of fiscal year)
  this.loadDistinctRoles();
}
    loadDistinctRoles() {
    // Use existing endpoint to get all staffed roles
    this.staffedRoleService.getStaffedRoles().subscribe({
      next: (staffedRoles) => {
        // Extract distinct role names and sort them
        const distinctRoles = [...new Set(staffedRoles.map(sr => sr.role))].sort();
        this.roleOptions = distinctRoles;
        this.roleOptionsObj = distinctRoles.map(r => ({ label: r, value: r, searchKey: r.toUpperCase().replace(/[\s\-]+/g, '') }));
        this.filteredRoleOptionsObj = [...this.roleOptionsObj];
      },
      error: (error) => {
        console.error('Error loading roles:', error);
      }
    });
  }
onRoleSearchInput(event: any) {
  const raw = (event.filter || '').trim().toUpperCase();

  if (!raw) {
    this.filteredRoleOptionsObj = [...this.roleOptionsObj];
    return;
  }

  const normalizedTerm = raw.replace(/[\s\-]+/g, '');

  this.filteredRoleOptionsObj = this.roleOptionsObj
    .filter(opt => opt.searchKey.includes(normalizedTerm))
    .map(opt => ({
      ...opt,
      searchKey: raw
    }));
}
  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.staffedRoles.addStaffedRole.errorMessage.title',
        'layout.configurations.staffedRoles.addStaffedRole.errorMessage.alreadyExists',
        'layout.configurations.staffedRoles.editStaffedRole.errorMessage.title',
        'layout.configurations.staffedRoles.editStaffedRole.errorMessage.alreadyExists',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

populateForm(staffedRole: StaffedRole) {
  const patch: any = {
    id: staffedRole.id,
    role: staffedRole.role,
    fiscalYear: staffedRole.fiscalYear,
    rateEuro: staffedRole.rateEuro ?? null,
  };

  this.orderedCurrencies.forEach(currency => {
    const key = 'rate' + currency.charAt(0).toUpperCase() + currency.slice(1).toLowerCase();
    const rawValue = (staffedRole as any)[key];
    const convertedValue = staffedRole.convertedAmount?.[currency.toUpperCase()]?.convertedAmount;
    
    console.log(`Currency ${currency}: key=${key}, rawValue=${rawValue}, convertedValue=${convertedValue}`);
    
    patch[key] = rawValue ?? convertedValue ?? null;
  });

  console.log('Patch object:', patch);
  this.form.patchValue(patch);
  console.log('After patchValue, form value:', this.form.getRawValue());
}

  onSave() {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach(control => control.markAsTouched());
      return;
    }
    if (this.isEditMode) {
      this.updateStaffedRole();
    } else {
      this.addStaffedRole();
    }
  }

  addStaffedRole() {
    this.staffedRoleService.addStaffedRole(this.form.value).subscribe({
      next: (staffedRole: StaffedRole) => this.ref.close(staffedRole),
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while saving the staffed role: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.staffedRoles.addStaffedRole.errorMessage.title'],
          detail: this.translations['layout.configurations.staffedRoles.addStaffedRole.errorMessage.alreadyExists'],
        });
      },
    });
  }

  updateStaffedRole() {
    this.staffedRoleService.updateStaffedRole(this.form.getRawValue()).subscribe({
      next: (staffedRole: StaffedRole) => this.ref.close(staffedRole),
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while updating the staffed role: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.staffedRoles.addStaffedRole.errorMessage.title'],
          detail: this.translations['layout.configurations.staffedRoles.addStaffedRole.errorMessage.alreadyExists'],
        });
      },
    });
  }

  isInvalid(key: string) {
    const control = this.form.get(key);
    return control?.invalid && (control?.dirty || control?.touched);
  }

  getErrorMessage(field: string) {
    return this.formErrors?.find((obj: { field: string }) => obj.field == field)?.errorMessage || '';
  }
 onCancel() {
    this.ref.close();
  }
}